import auth0, {
  getOrganizationKeyFromURL,
  redirectToAuth0Logout,
} from '@providers/auth0Client';
import { broadcastLogout } from '@providers/crossTabLogout';
import { cache } from '@providers/ovApolloProvider';
import { BootSpinner, Box, Typography } from 'hephaestus/web';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Path for IdP-initiated logout (client redirects here in the same tab or popup). */
export const IDP_LOGOUT_PATH = '/idp-logout';

const IDP_LOGOUT_RETURN_TO_PARAM = 'returnTo';

export const IDP_LOGOUT_CLOSE_WINDOW_PARAM = 'closeWindow';

export const IDP_LOGOUT_CLOSE_PENDING_KEY = 'idp-logout-close-pending';

/** Delays (ms from mount) for repeated window.close() attempts after Auth0 redirect. */
const CLOSE_ATTEMPT_DELAYS_MS = [0, 150, 400];

const LOG_PREFIX = '[idp-logout]';

/**
 * Detects the IdP logout entry point. Routed at the App root (see App.tsx), not
 * via react-router, so Auth0Provider and UserContextProvider never mount.
 */
export const isIdpLogoutRoute = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname === IDP_LOGOUT_PATH;

export const getReturnToFromQuery = (): string | null => {
  const value = new URLSearchParams(window.location.search).get(
    IDP_LOGOUT_RETURN_TO_PARAM,
  );
  if (!value?.trim()) {
    return null;
  }
  return value;
};

export const getDefaultLogoutReturnTo = (): string => {
  const { auth0RedirectUri } = getOrganizationKeyFromURL();
  return auth0RedirectUri || window.location.origin;
};

export const isCloseWindowRequested = (): boolean => {
  const value = new URLSearchParams(window.location.search)
    .get(IDP_LOGOUT_CLOSE_WINDOW_PARAM)
    ?.trim()
    .toLowerCase();
  return value === 'true' || value === '1';
};

export const isCloseWindowPending = (): boolean =>
  sessionStorage.getItem(IDP_LOGOUT_CLOSE_PENDING_KEY) === '1';

export const markCloseWindowPending = (): void => {
  sessionStorage.setItem(IDP_LOGOUT_CLOSE_PENDING_KEY, '1');
};

export const consumeCloseWindowPending = (): boolean => {
  if (!isCloseWindowPending()) {
    return false;
  }
  // Stale popup flag must not hijack a later same-tab visit with returnTo.
  if (getReturnToFromQuery() && !isCloseWindowRequested()) {
    sessionStorage.removeItem(IDP_LOGOUT_CLOSE_PENDING_KEY);
    return false;
  }
  sessionStorage.removeItem(IDP_LOGOUT_CLOSE_PENDING_KEY);
  return true;
};

export const getIdpLogoutLandingReturnTo = (): string =>
  `${window.location.origin}${IDP_LOGOUT_PATH}`;

/** Auth0 validates on logout. closeWindow mode always returns to OV /idp-logout. */
export const resolveAuth0LogoutReturnTo = (): string => {
  if (isCloseWindowRequested()) {
    return getIdpLogoutLandingReturnTo();
  }
  return getReturnToFromQuery() ?? getDefaultLogoutReturnTo();
};

const logIdpLogout = (message: string, details?: Record<string, unknown>) => {
  console.log(LOG_PREFIX, message, details ?? '');
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

/** Browsers only close tabs opened via window.open(). */
const tryCloseTab = () => {
  window.close();
};

export const IdpLogout = () => {
  const { t } = useTranslation('authentication');
  const logoutStartedRef = useRef(false);
  const closeAttemptsStartedRef = useRef(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [showCloseFallback, setShowCloseFallback] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const initializeAuthState = async () => {
      if (cancelled) return;

      const authenticated = await auth0.isAuthenticated();
      logIdpLogout('auth state ready', { isAuthenticated: authenticated });
      setIsAuthenticated(authenticated);
      setIsAuthReady(true);
    };

    void initializeAuthState();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthReady || isAuthenticated === null) {
      return;
    }

    const runClosePhase = async () => {
      if (closeAttemptsStartedRef.current) {
        return;
      }
      closeAttemptsStartedRef.current = true;

      logIdpLogout('closeWindow: attempting to close tab');

      let previousDelay = 0;
      for (const [index, delayMs] of CLOSE_ATTEMPT_DELAYS_MS.entries()) {
        await sleep(delayMs - previousDelay);
        previousDelay = delayMs;
        logIdpLogout(`closeWindow: close attempt ${index + 1}`);
        tryCloseTab();
      }

      logIdpLogout('closeWindow: tab still open — showing close fallback');
      setShowCloseFallback(true);
    };

    if (!isAuthenticated && consumeCloseWindowPending()) {
      logIdpLogout('closeWindow: post-Auth0 close phase');
      void runClosePhase();
      return;
    }

    if (logoutStartedRef.current) {
      return;
    }

    const closeWindow = isCloseWindowRequested();

    if (closeWindow && !isAuthenticated) {
      logoutStartedRef.current = true;
      logIdpLogout('closeWindow: unauthenticated — closing immediately');
      broadcastLogout();
      void runClosePhase();
      return;
    }

    logoutStartedRef.current = true;

    const performLogout = async () => {
      if (closeWindow && isAuthenticated) {
        markCloseWindowPending();
      }

      const returnTo = resolveAuth0LogoutReturnTo();
      const returnToSource = closeWindow
        ? 'closeWindow'
        : new URLSearchParams(window.location.search).has(
              IDP_LOGOUT_RETURN_TO_PARAM,
            )
          ? 'query'
          : 'default';

      logIdpLogout('calling Auth0 logout', {
        returnTo,
        returnToSource,
        isAuthenticated,
        closeWindow,
      });

      if (isAuthenticated) {
        await cache.reset();
        try {
          await auth0.logout({
            logoutParams: { returnTo, federated: false },
            openUrl(url: string) {
              broadcastLogout();
              window.location.assign(url);
            },
          });
          logIdpLogout('auth0.logout resolved without navigation (unexpected)');
        } catch (error) {
          broadcastLogout();
          console.error(LOG_PREFIX, 'auth0.logout failed', error);
        }
      } else {
        broadcastLogout();
      }

      redirectToAuth0Logout(returnTo);
    };

    void performLogout();
  }, [isAuthReady, isAuthenticated]);

  if (showCloseFallback) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          margin: 'auto',
          px: 3,
        }}
      >
        <Typography variant='bodyLarge' sx={{ textAlign: 'center' }}>
          {t('idpLogout.closeTabMessage')}
        </Typography>
      </Box>
    );
  }

  return <BootSpinner />;
};
